(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-rp-parts-finder]";
  const HELP_DEFAULT = "/pages/contact-us";

  const VERIFIED_STATUSES = new Set([
    "catalog",
    "manufacturer",
    "specialist",
    "approved"
  ]);

  const SUPPORTED_RESULTS = new Set([
    "exact_product",
    "product_family",
    "collection",
    "search",
    "measurement_guide",
    "fitment_help",
    "proposed_kit",
    "no_route"
  ]);

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[–—]/g, "-")
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ");

  const normalizeCompact = (value) =>
    normalizeText(value).replace(/\s+/g, "");

  const textMatches = (first, second) => {
    const a = normalizeText(first);
    const b = normalizeText(second);

    if (!a || !b) {
      return false;
    }

    return (
      a === b ||
      a.includes(b) ||
      b.includes(a) ||
      normalizeCompact(a) === normalizeCompact(b)
    );
  };

  const isBlank = (value) =>
    String(value ?? "").trim() === "";

  const isUnknownValue = (value) => {
    const normalized = normalizeText(value);

    return (
      normalized === "" ||
      normalized.includes("unknown") ||
      normalized.includes("not sure") ||
      normalized === "other"
    );
  };

  const asBoolean = (value, fallback = false) => {
    if (
      value === true ||
      value === "true" ||
      value === 1 ||
      value === "1"
    ) {
      return true;
    }

    if (
      value === false ||
      value === "false" ||
      value === 0 ||
      value === "0"
    ) {
      return false;
    }

    return fallback;
  };

  const asNumber = (value, fallback = 100) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const safeJsonParse = (value, fallback = null) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return fallback;
    }

    if (typeof value === "object") {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const splitValues = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter(Boolean);
    }

    return String(value || "")
      .split(/[\n,|]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const uniqueValues = (values) => [
    ...new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  ];

  const createOption = (value, label) => {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    return option;
  };

  const initFinder = (root) => {
    if (
      !root ||
      root.dataset.rpInitialized === "true"
    ) {
      return;
    }

    const data = window.RPPartsFinderData;

    if (!data) {
      return;
    }

    root.dataset.rpInitialized = "true";

    const searchUrl =
      root.dataset.rpSearchUrl ||
      "/search";

    const configuredHelpUrl =
      root.dataset.rpHelpUrl ||
      HELP_DEFAULT;

    const defaultCaution =
      root.dataset.rpDefaultCaution ||
      "Confirm fitment before ordering. Similar-looking trailer parts may use different dimensions, capacities, mounting patterns, bearings, seals, threads, electrical connections, or left/right orientation.";

    const resultHeading =
      root.dataset.rpResultHeading ||
      "Recommended next step";

    const goLabel =
      root.dataset.rpGoLabel ||
      "GO";

    const resetLabel =
      root.dataset.rpResetLabel ||
      "Start Over";

    const showRoutePath =
      root.dataset.rpShowPath === "true";

    const enableFitmentHelpPaths =
      root.dataset.rpFitmentPaths !== "false";

    const enableAnalytics =
      root.dataset.rpAnalytics === "true";

    const eventPrefix =
      root.dataset.rpEventPrefix ||
      "rp_parts_finder";

    const designMode =
      root.dataset.rpDesignMode === "true";

    const validationEnabled =
      root.dataset.rpValidation === "true";

    const sectionId =
      root.dataset.rpSectionId ||
      root.id ||
      "";

    const form =
      root.querySelector("[data-rp-finder-form]");

    const trailerSelect =
      root.querySelector("[data-rp-trailer]");

    const repairSelect =
      root.querySelector("[data-rp-repair]");

    const identifySelect =
      root.querySelector("[data-rp-identify]");

    const dynamicWrap =
      root.querySelector("[data-rp-dynamic-wrap]");

    const dynamicContainer =
      root.querySelector("[data-rp-dynamic]");

    const goButton =
      root.querySelector("[data-rp-go]");

    const statusElement =
      root.querySelector("[data-rp-status]");

    const quickForm =
      root.querySelector("[data-rp-quick-form]");

    const quickInput =
      root.querySelector("[data-rp-quick-input]");

    const resultElement =
      root.querySelector("[data-rp-result]");

    const resultTypeElement =
      root.querySelector("[data-rp-result-type]");

    const resultTitleElement =
      root.querySelector("[data-rp-result-title]");

    const resultPathElement =
      root.querySelector("[data-rp-result-path]");

    const resultCopyElement =
      root.querySelector("[data-rp-result-copy]");

    const resultNoteElement =
      root.querySelector("[data-rp-result-note]");

    const resultCautionElement =
      root.querySelector("[data-rp-result-caution]");

    const camberNoteElement =
      root.querySelector("[data-rp-camber-note]");

    const resultLink =
      root.querySelector("[data-rp-result-link]");

    const resultHelpLink =
      root.querySelector("[data-rp-result-help]");

    const resetButton =
      root.querySelector("[data-rp-reset]");

    const progressItems = [
      ...root.querySelectorAll("[data-rp-progress]")
    ];

    const diagnosticsElement =
      root.querySelector("[data-rp-diagnostics]");

    const diagnosticList =
      root.querySelector("[data-rp-diagnostic-list]");

    if (
      !form ||
      !trailerSelect ||
      !repairSelect ||
      !identifySelect ||
      !dynamicContainer ||
      !goButton
    ) {
      return;
    }

    const state = {
      trailer: "",
      area: "",
      identify: "",
      mode: "",
      method: "",
      task: null,
      questions: [],
      answers: {},
      submitted: false
    };

    const setDisabled = (element, disabled) => {
      if (!element) {
        return;
      }

      element.disabled = disabled;
      element.setAttribute(
        "aria-disabled",
        disabled ? "true" : "false"
      );
    };

    const setStatus = (message = "", type = "") => {
      if (!statusElement) {
        return;
      }

      statusElement.textContent = message;
      statusElement.classList.remove("is-error", "is-success");

      if (type) {
        statusElement.classList.add(`is-${type}`);
      }
    };

    const setTextVisibility = (element, value) => {
      if (!element) {
        return;
      }

      const text = String(value || "").trim();
      element.textContent = text;
      element.hidden = !text;
    };

    const buildSearchUrl = (query) => {
      const url = new URL(
        searchUrl,
        window.location.origin
      );

      url.searchParams.set("type", "product");
      url.searchParams.set("options[prefix]", "last");
      url.searchParams.set("q", String(query || "").trim());

      if (url.origin === window.location.origin) {
        return `${url.pathname}${url.search}${url.hash}`;
      }

      return url.toString();
    };

    const dispatchFinderEvent = (eventName, detail = {}) => {
      if (!enableAnalytics) {
        return;
      }

      const completeName =
        `${eventPrefix}:${eventName}`;

      const eventDetail = {
        sectionId,
        ...detail
      };

      root.dispatchEvent(
        new CustomEvent(
          completeName,
          {
            bubbles: true,
            detail: eventDetail
          }
        )
      );

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: completeName,
          ...eventDetail
        });
      }
    };

    const routeParseWarnings = [];

    const normalizeRoute = (route, index) => ({
      id: String(route.id || `route-${index + 1}`),
      source: String(route.source || "unknown"),
      sourceLabel: String(
        route.sourceLabel ||
        route.id ||
        `Route ${index + 1}`
      ),
      active: asBoolean(route.active, true),
      trailerType: String(route.trailerType || "").trim(),
      repairArea: String(route.repairArea || "").trim(),
      identifyMethod: String(route.identifyMethod || "").trim(),
      q1Label: String(route.q1Label || "").trim(),
      q1Value: String(route.q1Value || "").trim(),
      q2Label: String(route.q2Label || "").trim(),
      q2Value: String(route.q2Value || "").trim(),
      q3Label: String(route.q3Label || "").trim(),
      q3Value: String(route.q3Value || "").trim(),
      normalizedAttributes: safeJsonParse(
        route.normalizedAttributes,
        route.normalizedAttributes
      ),
      destinationType: String(route.destinationType || "").trim(),
      destinationUrl: String(route.destinationUrl || "").trim(),
      searchKeywords: String(route.searchKeywords || "").trim(),
      aliases: splitValues(route.aliases),
      priority: asNumber(route.priority, 100),
      resultType: String(route.resultType || "").trim(),
      verificationStatus: String(
        route.verificationStatus || ""
      ).trim(),
      fitmentNote: String(route.fitmentNote || "").trim(),
      verificationNote: String(
        route.verificationNote || ""
      ).trim(),
      assistanceUrl: String(route.assistanceUrl || "").trim(),
      evidenceNote: String(route.evidenceNote || "").trim(),
      referenceAvailable: asBoolean(
        route.referenceAvailable,
        true
      )
    });

    const configuredRoutes = [
      ...root.querySelectorAll("script[data-rp-route]")
    ]
      .map((node, index) => {
        try {
          return normalizeRoute(
            JSON.parse(node.textContent.trim()),
            index
          );
        } catch (error) {
          routeParseWarnings.push(
            `Route ${index + 1} contains invalid JSON and was ignored.`
          );

          return null;
        }
      })
      .filter(Boolean);

    const activeRoutes =
      configuredRoutes.filter((route) => route.active);

    const lookupLabel = (items, id, fallback = "") => {
      const match = items.find((item) => item.id === id);
      return match ? match.label : fallback;
    };

    const getTrailerLabel = (id) =>
      lookupLabel(data.trailerTypes, id, id);

    const getAreaLabel = (id) =>
      lookupLabel(data.areas, id, id);

    const getMethodLabel = (id) =>
      lookupLabel(data.methods, id, id);

    const getTask = (areaId, taskId) => {
      const areaTasks = data.tasks[areaId] || [];
      return areaTasks.find((item) => item.id === taskId) || null;
    };

    const getOrderedAreas = (trailerId) => {
      const preferred =
        data.trailerAreaPriority[trailerId] ||
        data.trailerAreaPriority.unknown ||
        [];

      const areaMap = new Map(
        data.areas.map((area) => [area.id, area])
      );

      const ordered = [];

      preferred.forEach((id) => {
        if (areaMap.has(id)) {
          ordered.push(areaMap.get(id));
          areaMap.delete(id);
        }
      });

      areaMap.forEach((area) => {
        ordered.push(area);
      });

      return ordered;
    };

    const getOrderedTasks = (areaId, trailerId) => {
      const areaTasks = [...(data.tasks[areaId] || [])];

      return areaTasks.sort((a, b) => {
        const aPriority =
          Array.isArray(a.trailerPriority) &&
          a.trailerPriority.includes(trailerId)
            ? 1
            : 0;

        const bPriority =
          Array.isArray(b.trailerPriority) &&
          b.trailerPriority.includes(trailerId)
            ? 1
            : 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }

        return a.label.localeCompare(b.label);
      });
    };

    const resetSelect = (select, placeholder) => {
      select.innerHTML = "";
      select.appendChild(createOption("", placeholder));
      select.value = "";
    };

    const populateTrailerSelect = () => {
      resetSelect(trailerSelect, "Select Trailer");

      data.trailerTypes.forEach((trailer) => {
        trailerSelect.appendChild(
          createOption(trailer.id, trailer.label)
        );
      });

      setDisabled(trailerSelect, false);
    };

    const populateRepairSelect = () => {
      resetSelect(repairSelect, "Select Repair Area");

      if (!state.trailer) {
        setDisabled(repairSelect, true);
        return;
      }

      getOrderedAreas(state.trailer).forEach((area) => {
        repairSelect.appendChild(
          createOption(area.id, area.label)
        );
      });

      setDisabled(repairSelect, false);
    };

    const populateIdentifySelect = () => {
      resetSelect(identifySelect, "Select Part Type");

      if (
        !state.trailer ||
        !state.area
      ) {
        setDisabled(identifySelect, true);
        return;
      }

      const areaTasks =
        getOrderedTasks(state.area, state.trailer);

      if (areaTasks.length) {
        const taskGroup =
          document.createElement("optgroup");

        taskGroup.label =
          "Choose a part or repair task";

        areaTasks.forEach((item) => {
          taskGroup.appendChild(
            createOption(
              `task:${item.id}`,
              item.label
            )
          );
        });

        identifySelect.appendChild(taskGroup);
      }

      const methodGroup =
        document.createElement("optgroup");

      methodGroup.label =
        "Other ways to identify the part";

      data.methods
        .filter((method) => method.id !== "task")
        .forEach((method) => {
          methodGroup.appendChild(
            createOption(
              `method:${method.id}`,
              method.label
            )
          );
        });

      identifySelect.appendChild(methodGroup);
      setDisabled(identifySelect, false);
    };

    const updateProgress = (matched = false) => {
      let activeStep = 1;

      if (state.trailer) {
        activeStep = 2;
      }

      if (state.trailer && state.area) {
        activeStep = 3;
      }

      if (
        state.trailer &&
        state.area &&
        state.identify
      ) {
        activeStep = 4;
      }

      progressItems.forEach((item, index) => {
        const step = index + 1;

        item.classList.remove(
          "is-active",
          "is-complete"
        );

        item.removeAttribute("aria-current");

        if (matched || step < activeStep) {
          item.classList.add("is-complete");
        } else if (step === activeStep) {
          item.classList.add("is-active");
          item.setAttribute("aria-current", "step");
        }
      });
    };

    const conditionIsMet = (condition) => {
      if (!condition) {
        return true;
      }

      if (typeof condition === "function") {
        return Boolean(condition(state.answers));
      }

      if (Array.isArray(condition)) {
        return condition.every(conditionIsMet);
      }

      if (typeof condition !== "object") {
        return true;
      }

      const currentValue =
        state.answers[condition.key] || "";

      if (
        Object.prototype.hasOwnProperty.call(
          condition,
          "equals"
        )
      ) {
        return textMatches(
          currentValue,
          condition.equals
        );
      }

      if (Array.isArray(condition.in)) {
        return condition.in.some((value) =>
          textMatches(currentValue, value)
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(
          condition,
          "not"
        )
      ) {
        return !textMatches(
          currentValue,
          condition.not
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(
          condition,
          "includes"
        )
      ) {
        return normalizeText(currentValue).includes(
          normalizeText(condition.includes)
        );
      }

      return true;
    };

    const updateConditionalFields = () => {
      [
        ...dynamicContainer.querySelectorAll(
          "[data-rp-question-field]"
        )
      ].forEach((field) => {
        const question = field.rpQuestion;

        if (!question) {
          return;
        }

        const visible =
          conditionIsMet(question.showWhen);

        field.hidden = !visible;

        const input =
          field.querySelector("[data-rp-question-input]");

        if (input) {
          input.disabled = !visible;

          if (!visible) {
            state.answers[question.key] = "";
            input.value = "";
          }
        }
      });
    };

    const makeQuestionField = (question, index) => {
      if (question.type === "note") {
        const note = document.createElement("div");

        note.className =
          "rp-gpf__inline-note rp-gpf__field--dynamic";

        note.textContent = question.text || "";
        note.dataset.rpQuestionField = "true";
        note.rpQuestion = question;

        return note;
      }

      const field = document.createElement("div");

      field.className =
        "rp-gpf__field rp-gpf__field--dynamic";

      field.dataset.rpQuestionField = "true";
      field.rpQuestion = question;

      const inputId =
        `${root.id}-question-${index + 1}`;

      const label = document.createElement("label");

      label.className = "rp-gpf__label";
      label.htmlFor = inputId;
      label.textContent = question.label;

      field.appendChild(label);

      let input;

      if (question.type === "select") {
        const selectWrap =
          document.createElement("div");

        selectWrap.className =
          "rp-gpf__select-wrap";

        input = document.createElement("select");
        input.id = inputId;

        input.appendChild(
          createOption(
            "",
            `Choose ${question.label}`
          )
        );

        (question.options || []).forEach((optionValue) => {
          if (
            typeof optionValue === "object" &&
            optionValue !== null
          ) {
            input.appendChild(
              createOption(
                optionValue.value,
                optionValue.label
              )
            );
          } else {
            input.appendChild(
              createOption(
                optionValue,
                optionValue
              )
            );
          }
        });

        selectWrap.appendChild(input);
        field.appendChild(selectWrap);
      } else {
        input = document.createElement("input");
        input.id = inputId;
        input.type =
          question.type === "number"
            ? "number"
            : "text";

        input.placeholder =
          question.placeholder || "";

        if (question.inputmode) {
          input.inputMode = question.inputmode;
        }

        field.appendChild(input);
      }

      input.dataset.rpQuestionInput = "true";
      input.dataset.rpQuestionKey = question.key;
      input.dataset.rpQuestionLabel = question.label;
      input.required = question.required !== false;
      input.value = state.answers[question.key] || "";

      const handleInput = () => {
        state.answers[question.key] =
          input.value.trim();

        updateConditionalFields();
        updateGoState();

        dispatchFinderEvent(
          "answer_change",
          {
            trailer: state.trailer,
            area: state.area,
            identify: state.identify,
            question: question.key,
            value: input.value.trim()
          }
        );
      };

      input.addEventListener("change", handleInput);
      input.addEventListener("input", handleInput);

      if (question.help) {
        const help = document.createElement("p");

        help.className = "rp-gpf__field-help";
        help.textContent = question.help;
        field.appendChild(help);
      }

      return field;
    };

    const getQuestionsForSelection = () => {
      if (
        state.mode === "task" &&
        state.task
      ) {
        return state.task.questions || [];
      }

      if (state.mode === "method") {
        return data.methodQuestions[state.method] || [];
      }

      return [];
    };

    const renderDynamicQuestions = () => {
      dynamicContainer.innerHTML = "";
      state.questions = getQuestionsForSelection();

      if (!state.questions.length) {
        if (dynamicWrap) {
          dynamicWrap.hidden = true;
        }

        updateGoState();
        return;
      }

      state.questions.forEach((question, index) => {
        dynamicContainer.appendChild(
          makeQuestionField(question, index)
        );
      });

      if (dynamicWrap) {
        dynamicWrap.hidden = false;
      }

      updateConditionalFields();
      updateGoState();
    };

    const getVisibleQuestionFields = () => [
      ...dynamicContainer.querySelectorAll(
        "[data-rp-question-field]"
      )
    ].filter((field) => !field.hidden);

    const validateDynamicQuestions = () => {
      const missing = [];

      getVisibleQuestionFields().forEach((field) => {
        const question = field.rpQuestion;

        if (
          !question ||
          question.type === "note" ||
          question.required === false
        ) {
          return;
        }

        const input =
          field.querySelector("[data-rp-question-input]");

        if (
          !input ||
          !input.value.trim()
        ) {
          missing.push(question.label);
        }
      });

      return {
        valid: missing.length === 0,
        missing
      };
    };

    const updateGoState = () => {
      const baseReady = Boolean(
        state.trailer &&
        state.area &&
        state.identify
      );

      const dynamicValidation =
        validateDynamicQuestions();

      setDisabled(
        goButton,
        !(baseReady && dynamicValidation.valid)
      );

      updateProgress(false);
    };

    const collectAnswerRecords = () => {
      const records = [];

      getVisibleQuestionFields().forEach((field) => {
        const question = field.rpQuestion;

        if (
          !question ||
          question.type === "note"
        ) {
          return;
        }

        const input =
          field.querySelector("[data-rp-question-input]");

        if (!input) {
          return;
        }

        const value = input.value.trim();

        state.answers[question.key] = value;

        records.push({
          key: question.key,
          label: question.label,
          value
        });
      });

      return records;
    };

    const meaningfulAnswerValues = (answerRecords) =>
      answerRecords
        .map((record) => record.value)
        .filter(
          (value) =>
            value &&
            !isUnknownValue(value)
        );

    const routeDimensionMatches = (
      routeValue,
      candidates
    ) => {
      if (isBlank(routeValue)) {
        return true;
      }

      const normalizedRoute =
        normalizeText(routeValue);

      if (
        [
          "all",
          "any",
          "any trailer",
          "any repair area",
          "any method"
        ].includes(normalizedRoute)
      ) {
        return true;
      }

      return candidates.some(
        (candidate) =>
          candidate &&
          textMatches(routeValue, candidate)
      );
    };

    const findAnswerByLabel = (
      answerRecords,
      label
    ) => {
      if (!label) {
        return null;
      }

      return (
        answerRecords.find(
          (record) =>
            textMatches(record.label, label) ||
            textMatches(record.key, label)
        ) || null
      );
    };

    const routeQuestionMatches = (
      route,
      answerRecords,
      number
    ) => {
      const routeLabel = route[`q${number}Label`];
      const routeValue = route[`q${number}Value`];

      if (!routeLabel && !routeValue) {
        return {
          matched: true,
          score: 0
        };
      }

      let answer = null;

      if (routeLabel) {
        answer = findAnswerByLabel(
          answerRecords,
          routeLabel
        );
      }

      if (!answer && routeValue) {
        answer =
          answerRecords.find((record) =>
            textMatches(record.value, routeValue)
          ) || null;
      }

      if (!answer) {
        return {
          matched: false,
          score: 0
        };
      }

      if (
        routeValue &&
        !textMatches(answer.value, routeValue)
      ) {
        return {
          matched: false,
          score: 0
        };
      }

      return {
        matched: true,
        score:
          (routeLabel ? 12 : 0) +
          (routeValue ? 24 : 0)
      };
    };

    const normalizedAttributesMatch = (
      route,
      answerRecords
    ) => {
      const attributes = route.normalizedAttributes;

      if (
        !attributes ||
        typeof attributes !== "object" ||
        Array.isArray(attributes)
      ) {
        return {
          matched: true,
          score: 0
        };
      }

      const answerMap = new Map();

      answerRecords.forEach((record) => {
        answerMap.set(
          normalizeText(record.key),
          record.value
        );

        answerMap.set(
          normalizeText(record.label),
          record.value
        );
      });

      let score = 0;

      for (
        const [key, expectedValue]
        of Object.entries(attributes)
      ) {
        const actualValue =
          answerMap.get(normalizeText(key));

        if (
          actualValue === undefined ||
          !textMatches(actualValue, expectedValue)
        ) {
          return {
            matched: false,
            score: 0
          };
        }

        score += 20;
      }

      return {
        matched: true,
        score
      };
    };

    const scoreRoute = (route, answerRecords) => {
      const trailerCandidates = [
        state.trailer,
        getTrailerLabel(state.trailer)
      ];

      const areaCandidates = [
        state.area,
        getAreaLabel(state.area)
      ];

      const identifyCandidates = [
        state.identify,
        state.mode,
        state.method,
        state.task?.id,
        state.task?.label,
        state.method
          ? getMethodLabel(state.method)
          : ""
      ].filter(Boolean);

      if (
        !routeDimensionMatches(
          route.trailerType,
          trailerCandidates
        )
      ) {
        return null;
      }

      if (
        !routeDimensionMatches(
          route.repairArea,
          areaCandidates
        )
      ) {
        return null;
      }

      if (
        !routeDimensionMatches(
          route.identifyMethod,
          identifyCandidates
        )
      ) {
        return null;
      }

      let score = 0;

      if (route.trailerType) {
        score += 80;
      }

      if (route.repairArea) {
        score += 90;
      }

      if (route.identifyMethod) {
        score += 100;
      }

      for (
        let number = 1;
        number <= 3;
        number += 1
      ) {
        const questionMatch =
          routeQuestionMatches(
            route,
            answerRecords,
            number
          );

        if (!questionMatch.matched) {
          return null;
        }

        score += questionMatch.score;
      }

      const attributeMatch =
        normalizedAttributesMatch(
          route,
          answerRecords
        );

      if (!attributeMatch.matched) {
        return null;
      }

      score += attributeMatch.score;

      if (route.destinationUrl) {
        score += 8;
      }

      if (
        VERIFIED_STATUSES.has(
          normalizeText(route.verificationStatus)
        )
      ) {
        score += 5;
      }

      return {
        route,
        score
      };
    };

    const findBestConfiguredRoute = (answerRecords) => {
      const matches = activeRoutes
        .map((route) =>
          scoreRoute(route, answerRecords)
        )
        .filter(Boolean)
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }

          return a.route.priority - b.route.priority;
        });

      return matches.length
        ? matches[0].route
        : null;
    };

    const buildTaskSearchQuery = (
      taskItem,
      answerRecords
    ) => {
      const partNumberRecord =
        answerRecords.find(
          (record) =>
            record.key === "part_number"
        );

      if (partNumberRecord?.value) {
        return uniqueValues([
          partNumberRecord.value,
          taskItem?.searchBase,
          taskItem?.label
        ]).join(" ");
      }

      return uniqueValues([
        taskItem?.searchBase,
        taskItem?.label,
        ...meaningfulAnswerValues(answerRecords)
      ]).join(" ");
    };

    const buildMethodSearchQuery = (answerRecords) =>
      uniqueValues([
        getAreaLabel(state.area),
        ...meaningfulAnswerValues(answerRecords),
        "trailer part"
      ]).join(" ");

    const inferConfiguredResultType = (route) => {
      if (
        route.resultType &&
        SUPPORTED_RESULTS.has(route.resultType)
      ) {
        return route.resultType;
      }

      const type =
        normalizeText(route.destinationType);

      if (type === "product") {
        return "exact_product";
      }

      if (type === "collection") {
        return "collection";
      }

      if (type === "page") {
        return "measurement_guide";
      }

      if (route.searchKeywords) {
        return "search";
      }

      return "product_family";
    };

    const resultTypeLabel = (type) => {
      const labels = {
        exact_product: "Exact product route",
        product_family: "Product family",
        collection: "Recommended collection",
        search: "Catalog search",
        measurement_guide: "Measurement guide",
        fitment_help: "Fitment assistance",
        proposed_kit: "Proposed kit",
        no_route: "Assisted identification"
      };

      return labels[type] || resultHeading;
    };

    const resolveConfiguredRoute = (
      route,
      answerRecords
    ) => {
      const verified =
        VERIFIED_STATUSES.has(
          normalizeText(route.verificationStatus)
        );

      const resultType =
        inferConfiguredResultType(route);

      let destination = route.destinationUrl;

      if (
        !destination &&
        route.searchKeywords
      ) {
        destination =
          buildSearchUrl(route.searchKeywords);
      }

      if (!destination) {
        destination =
          route.assistanceUrl ||
          configuredHelpUrl;
      }

      const cautionParts = [
        route.fitmentNote ||
        state.task?.fitment ||
        defaultCaution
      ];

      if (!verified) {
        cautionParts.push(
          "Verify before publishing."
        );
      }

      if (route.referenceAvailable === false) {
        cautionParts.push(
          "The configured Shopify reference is unavailable. Confirm the product, collection, or page destination."
        );
      }

      if (resultType === "proposed_kit") {
        cautionParts.push(
          "Proposed kit — verify every included component, quantity, fitment boundary, and destination before publishing."
        );
      }

      return {
        source: "configured_route",
        resultType,
        title:
          route.sourceLabel ||
          state.task?.resultTitle ||
          resultHeading,
        destination,
        copy: route.searchKeywords
          ? `Matched using the configured route and catalog keywords: ${route.searchKeywords}.`
          : "A configured R&P finder route matched the selected trailer, repair area, part type, and supplied details.",
        note:
          route.verificationNote ||
          (verified ? "" : "Verify before publishing."),
        caution: cautionParts.join(" "),
        assistanceUrl:
          route.assistanceUrl ||
          configuredHelpUrl,
        verified,
        routeId: route.id,
        routeSource: route.source,
        evidenceNote: route.evidenceNote,
        camber: Boolean(state.task?.camber),
        answerRecords
      };
    };

    const resolveTaskFallback = (answerRecords) => {
      const taskItem = state.task;

      const query =
        buildTaskSearchQuery(
          taskItem,
          answerRecords
        );

      const destination =
        taskItem.collection ||
        buildSearchUrl(query);

      return {
        source: "task_fallback",
        resultType:
          taskItem.resultType ||
          (taskItem.collection ? "collection" : "search"),
        title:
          taskItem.resultTitle ||
          taskItem.label,
        destination,
        copy: taskItem.collection
          ? "This selection routes to the closest matching R&P product family. Use the supplied dimensions and part numbers to narrow the products shown."
          : "The finder prepared a Shopify product search using the selected repair task and supplied details.",
        note:
          taskItem.verificationNote ||
          "",
        caution:
          `${taskItem.fitment || defaultCaution} Verify the collection route and product fitment before publishing.`,
        assistanceUrl: configuredHelpUrl,
        verified: false,
        routeId: "",
        routeSource: "built_in_task",
        evidenceNote: "",
        camber: Boolean(taskItem.camber),
        query,
        answerRecords
      };
    };

    const resolveMethodFallback = (answerRecords) => {
      if (state.method === "help") {
        return {
          source: "method_fallback",
          resultType: "fitment_help",
          title: "Ask R&P to help identify the part",
          destination: configuredHelpUrl,
          copy:
            "Bring the photos, measurements, labels, casting numbers, axle tag, or other evidence you selected. Additional information may be required before recommending a part.",
          note:
            "Photos and measurements can support identification but may not prove capacity, internal construction, or compatibility.",
          caution: defaultCaution,
          assistanceUrl: configuredHelpUrl,
          verified: false,
          camber: false,
          answerRecords
        };
      }

      const query =
        buildMethodSearchQuery(answerRecords);

      return {
        source: "method_fallback",
        resultType: "search",
        title:
          state.method === "number"
            ? "Search the R&P catalog by part number"
            : state.method === "spec"
              ? "Search by verified specifications"
              : "Search by repair description",
        destination: buildSearchUrl(query),
        copy:
          "The finder prepared a product search from the information supplied. Review the product details and confirm fitment before ordering.",
        note:
          state.method === "number"
            ? "A similar SKU, casting, bearing, seal, or manufacturer number does not automatically confirm interchangeability."
            : "",
        caution: defaultCaution,
        assistanceUrl: configuredHelpUrl,
        verified: false,
        camber: false,
        query,
        answerRecords
      };
    };

    const resolveFinderResult = () => {
      const answerRecords =
        collectAnswerRecords();

      const configuredRoute =
        findBestConfiguredRoute(answerRecords);

      if (configuredRoute) {
        return resolveConfiguredRoute(
          configuredRoute,
          answerRecords
        );
      }

      if (
        state.mode === "task" &&
        state.task
      ) {
        return resolveTaskFallback(answerRecords);
      }

      if (state.mode === "method") {
        return resolveMethodFallback(answerRecords);
      }

      return {
        source: "no_route",
        resultType: "no_route",
        title: "Ask R&P to confirm the correct part",
        destination: configuredHelpUrl,
        copy:
          "The finder could not create a safe catalog route from the current selections.",
        note:
          "Provide photos, measurements, stamped numbers, and the trailer application.",
        caution: defaultCaution,
        assistanceUrl: configuredHelpUrl,
        verified: false,
        camber: false,
        answerRecords
      };
    };

    const buildResultPath = () => {
      const parts = [
        getTrailerLabel(state.trailer),
        getAreaLabel(state.area)
      ];

      if (
        state.mode === "task" &&
        state.task
      ) {
        parts.push(state.task.label);
      } else if (state.method) {
        parts.push(getMethodLabel(state.method));
      }

      return parts
        .filter(Boolean)
        .join(" → ");
    };

    const renderResult = (result) => {
      if (
        !resultElement ||
        !resultLink
      ) {
        return;
      }

      if (resultTypeElement) {
        resultTypeElement.textContent =
          resultTypeLabel(result.resultType);
      }

      if (resultTitleElement) {
        resultTitleElement.textContent =
          result.title ||
          resultHeading;
      }

      if (
        showRoutePath &&
        resultPathElement
      ) {
        resultPathElement.textContent =
          buildResultPath();

        resultPathElement.hidden = false;
      } else if (resultPathElement) {
        resultPathElement.textContent = "";
        resultPathElement.hidden = true;
      }

      setTextVisibility(
        resultCopyElement,
        result.copy
      );

      setTextVisibility(
        resultNoteElement,
        result.note
      );

      setTextVisibility(
        resultCautionElement,
        result.caution
      );

      if (camberNoteElement) {
        if (result.camber) {
          camberNoteElement.textContent =
            data.camberNote;

          camberNoteElement.hidden = false;
        } else {
          camberNoteElement.textContent = "";
          camberNoteElement.hidden = true;
        }
      }

      resultLink.href =
        result.destination ||
        configuredHelpUrl;

      resultLink.textContent = goLabel;

      if (
        result.resultType === "fitment_help" ||
        result.resultType === "no_route"
      ) {
        resultLink.textContent =
          "Get Fitment Help";
      }

      if (resultHelpLink) {
        const shouldShowHelp =
          enableFitmentHelpPaths &&
          Boolean(result.assistanceUrl) &&
          (
            !result.verified ||
            result.resultType === "search" ||
            result.resultType === "product_family" ||
            result.resultType === "collection"
          );

        resultHelpLink.hidden =
          !shouldShowHelp;

        if (shouldShowHelp) {
          resultHelpLink.href =
            result.assistanceUrl;
        }
      }

      resultElement.hidden = false;
      state.submitted = true;

      updateProgress(true);

      setStatus(
        "A recommended catalog path is ready below.",
        "success"
      );

      dispatchFinderEvent(
        "result",
        {
          trailer: state.trailer,
          repairArea: state.area,
          identify: state.identify,
          mode: state.mode,
          method: state.method,
          task: state.task?.id || "",
          resultType: result.resultType,
          source: result.source,
          routeId: result.routeId || "",
          destination: result.destination || "",
          verified: Boolean(result.verified)
        }
      );

      if (
        window.matchMedia(
          "(max-width: 720px)"
        ).matches
      ) {
        resultElement.scrollIntoView({
          behavior: window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches
            ? "auto"
            : "smooth",
          block: "start"
        });
      }
    };

    const hideResult = () => {
      if (resultElement) {
        resultElement.hidden = true;
      }

      state.submitted = false;
    };

    const clearDynamicSelection = () => {
      state.identify = "";
      state.mode = "";
      state.method = "";
      state.task = null;
      state.questions = [];
      state.answers = {};

      dynamicContainer.innerHTML = "";

      if (dynamicWrap) {
        dynamicWrap.hidden = true;
      }

      hideResult();
    };

    trailerSelect.addEventListener(
      "change",
      () => {
        state.trailer = trailerSelect.value;
        state.area = "";

        clearDynamicSelection();
        populateRepairSelect();

        resetSelect(
          identifySelect,
          "Select Part Type"
        );

        setDisabled(identifySelect, true);
        setStatus("");
        updateGoState();

        dispatchFinderEvent(
          "trailer_change",
          {
            trailer: state.trailer
          }
        );
      }
    );

    repairSelect.addEventListener(
      "change",
      () => {
        state.area = repairSelect.value;

        clearDynamicSelection();
        populateIdentifySelect();
        setStatus("");
        updateGoState();

        dispatchFinderEvent(
          "repair_area_change",
          {
            trailer: state.trailer,
            repairArea: state.area
          }
        );
      }
    );

    identifySelect.addEventListener(
      "change",
      () => {
        state.identify = identifySelect.value;
        state.mode = "";
        state.method = "";
        state.task = null;
        state.questions = [];
        state.answers = {};

        hideResult();
        setStatus("");

        if (
          state.identify.startsWith("task:")
        ) {
          const taskId =
            state.identify.slice(5);

          state.mode = "task";
          state.task =
            getTask(state.area, taskId);
        } else if (
          state.identify.startsWith("method:")
        ) {
          state.mode = "method";
          state.method =
            state.identify.slice(7);
        }

        renderDynamicQuestions();

        dispatchFinderEvent(
          "identify_change",
          {
            trailer: state.trailer,
            repairArea: state.area,
            identify: state.identify,
            mode: state.mode,
            task: state.task?.id || "",
            method: state.method
          }
        );
      }
    );

    form.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();

        const validation =
          validateDynamicQuestions();

        if (!state.trailer) {
          setStatus(
            "Select a trailer type.",
            "error"
          );

          trailerSelect.focus();
          return;
        }

        if (!state.area) {
          setStatus(
            "Select the repair area.",
            "error"
          );

          repairSelect.focus();
          return;
        }

        if (!state.identify) {
          setStatus(
            "Select a part type or identification method.",
            "error"
          );

          identifySelect.focus();
          return;
        }

        if (!validation.valid) {
          setStatus(
            `Complete the required field: ${validation.missing[0]}.`,
            "error"
          );

          const firstMissing =
            getVisibleQuestionFields()
              .map((field) =>
                field.querySelector(
                  "[data-rp-question-input]"
                )
              )
              .find(
                (input) =>
                  input &&
                  input.required &&
                  !input.value.trim()
              );

          firstMissing?.focus();
          return;
        }

        renderResult(resolveFinderResult());
      }
    );

    if (
      quickForm &&
      quickInput
    ) {
      quickForm.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();

          const query =
            quickInput.value.trim();

          if (!query) {
            quickInput.focus();
            return;
          }

          dispatchFinderEvent(
            "quick_search",
            { query }
          );

          window.location.assign(
            buildSearchUrl(query)
          );
        }
      );
    }

    resultLink?.addEventListener(
      "click",
      () => {
        dispatchFinderEvent(
          "result_click",
          {
            trailer: state.trailer,
            repairArea: state.area,
            identify: state.identify,
            destination: resultLink.href
          }
        );
      }
    );

    resultHelpLink?.addEventListener(
      "click",
      () => {
        dispatchFinderEvent(
          "fitment_help_click",
          {
            trailer: state.trailer,
            repairArea: state.area,
            identify: state.identify,
            destination: resultHelpLink.href
          }
        );
      }
    );

    const resetFinder = () => {
      state.trailer = "";
      state.area = "";
      state.identify = "";
      state.mode = "";
      state.method = "";
      state.task = null;
      state.questions = [];
      state.answers = {};
      state.submitted = false;

      populateTrailerSelect();

      resetSelect(
        repairSelect,
        "Select Repair Area"
      );

      resetSelect(
        identifySelect,
        "Select Part Type"
      );

      setDisabled(repairSelect, true);
      setDisabled(identifySelect, true);
      setDisabled(goButton, true);

      dynamicContainer.innerHTML = "";

      if (dynamicWrap) {
        dynamicWrap.hidden = true;
      }

      hideResult();
      setStatus("");
      updateProgress(false);
      trailerSelect.focus();

      dispatchFinderEvent("reset", {});
    };

    if (resetButton) {
      resetButton.textContent = resetLabel;
      resetButton.addEventListener(
        "click",
        resetFinder
      );
    }

    const buildDiagnostics = () => {
      if (
        !designMode ||
        !validationEnabled ||
        !diagnosticsElement ||
        !diagnosticList
      ) {
        return;
      }

      const warnings = [...routeParseWarnings];

      configuredRoutes.forEach((route) => {
        const routeName =
          route.sourceLabel ||
          route.id;

        if (!route.active) {
          return;
        }

        if (
          route.resultType &&
          !SUPPORTED_RESULTS.has(route.resultType)
        ) {
          warnings.push(
            `${routeName}: unsupported result type "${route.resultType}".`
          );
        }

        if (
          route.referenceAvailable === false
        ) {
          warnings.push(
            `${routeName}: configured Shopify reference is unavailable.`
          );
        }

        if (
          !route.destinationUrl &&
          !route.searchKeywords &&
          !route.assistanceUrl
        ) {
          warnings.push(
            `${routeName}: no destination, search keywords, or assistance URL is configured.`
          );
        }

        if (
          !route.trailerType &&
          !route.repairArea &&
          !route.identifyMethod
        ) {
          warnings.push(
            `${routeName}: route has no matching criteria and may match too broadly.`
          );
        }

        if (
          route.resultType === "proposed_kit"
        ) {
          warnings.push(
            `${routeName}: proposed kit routes require full BOM and fitment verification.`
          );
        }

        if (
          route.verificationStatus &&
          !VERIFIED_STATUSES.has(
            normalizeText(route.verificationStatus)
          )
        ) {
          warnings.push(
            `${routeName}: verification status "${route.verificationStatus}" is not treated as verified.`
          );
        }

        if (
          !route.evidenceNote &&
          route.source.startsWith("metaobject:")
        ) {
          warnings.push(
            `${routeName}: no source-evidence note is recorded.`
          );
        }
      });

      if (!activeRoutes.length) {
        warnings.push(
          "No active Theme Editor or metaobject routes were found. Built-in collection and search fallbacks will still operate."
        );
      }

      diagnosticList.innerHTML = "";

      warnings.forEach((warning) => {
        const item =
          document.createElement("li");

        item.textContent = warning;
        diagnosticList.appendChild(item);
      });

      diagnosticsElement.hidden =
        warnings.length === 0;
    };

    populateTrailerSelect();

    resetSelect(
      repairSelect,
      "Select Repair Area"
    );

    resetSelect(
      identifySelect,
      "Select Part Type"
    );

    setDisabled(repairSelect, true);
    setDisabled(identifySelect, true);
    setDisabled(goButton, true);

    if (dynamicWrap) {
      dynamicWrap.hidden = true;
    }

    hideResult();
    setStatus("");
    updateProgress(false);
    buildDiagnostics();

    dispatchFinderEvent(
      "initialized",
      {
        routeCount: activeRoutes.length
      }
    );
  };

  const initAll = (scope = document) => {
    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initFinder(scope);
    }

    scope
      .querySelectorAll?.(ROOT_SELECTOR)
      .forEach(initFinder);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll(document),
      { once: true }
    );
  } else {
    initAll(document);
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAll(event.target);
    }
  );
})();